import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import CredentialTest from '../../components/CredentialTest';

const PROVIDER_META = {
  github: {
    label:       'GitHub',
    color:       '#238636',
    description: 'Connect to GitHub to sync repositories from a user account or organisation.',
    tokenLabel:  'Personal Access Token',
    tokenHint:   'Requires repo scope (or read:org for organisation repos).',
    orgLabel:    'Organisation / User',
    orgHint:     'Leave blank to fetch all repos for the authenticated user.',
    showBaseUrl: false,
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
      </svg>
    ),
  },
  gitlab: {
    label:       'GitLab',
    color:       '#e24329',
    description: 'Connect to GitLab to sync repositories from a group or all projects the token can access.',
    tokenLabel:  'Personal / Group Access Token',
    tokenHint:   'Requires read_api scope.',
    orgLabel:    'Group / Namespace',
    orgHint:     'Leave blank to fetch all projects accessible to the token.',
    showBaseUrl: true,
    baseUrlPlaceholder: 'https://gitlab.com',
    baseUrlHint: 'Change only for self-hosted GitLab instances.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51L23 13.45a.84.84 0 0 1-.35.94z"/>
      </svg>
    ),
  },
  gitea: {
    label:       'Gitea',
    color:       '#609926',
    description: 'Connect to a self-hosted Gitea (or Forgejo) instance to sync repositories.',
    tokenLabel:  'Access Token',
    tokenHint:   'Create at Gitea → Settings → Applications. Needs repository read access.',
    orgLabel:    'Organisation',
    orgHint:     'Leave blank to fetch every repo the token can see.',
    showBaseUrl: true,
    baseUrlPlaceholder: 'https://gitea.example.com:3000',
    baseUrlHint: 'Required. Include the port if the instance uses one.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M4.03 5.22a2.6 2.6 0 0 0-2.6 2.6v1.55a6.6 6.6 0 0 0 5.9 6.56 6.3 6.3 0 0 0 2.53 3.2l-.9 2.1a.8.8 0 0 0 .74 1.1h4.6a.8.8 0 0 0 .73-1.1l-3.6-8.4 2.3-.98a.8.8 0 0 0 .42-1.05l-1.8-4.2a.8.8 0 0 0-1.05-.42l-2.3.99-.5-1.16a.8.8 0 0 0-1.05-.42l-1.1.47a2.6 2.6 0 0 0-2.22-1.24H4.03zm0 1.6h1.5c.34 0 .65.14.87.35L5.1 7.7a.8.8 0 0 0-.42 1.05l1.9 4.43a5 5 0 0 1-3.55-4.36V7.82c0-.55.45-1 1-1zm3.83 1.9 4.63-1.98 1.17 2.73-4.63 1.98-1.17-2.73z"/>
      </svg>
    ),
  },
  bitbucket: {
    label:       'Bitbucket',
    color:       '#0052cc',
    description: 'Connect to Bitbucket to sync repositories from a workspace.',
    tokenLabel:  'API Token',
    tokenHint:   'Create an Atlassian API token with Bitbucket scopes. App passwords are deprecated.',
    orgLabel:    'Workspace',
    orgHint:     'Required. The Bitbucket workspace slug (e.g. mycompany).',
    showBaseUrl: false,
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
        <path d="M.778 1.213a.768.768 0 0 0-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 0 0 .77-.646l3.27-20.03a.768.768 0 0 0-.768-.891L.778 1.213zM14.52 15.53H9.522L8.17 8.466h7.561l-1.211 7.064z"/>
      </svg>
    ),
  },
};

function ToggleSwitch({ enabled, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      style={{
        width: 44, height: 24, borderRadius: 999, padding: 2,
        background: enabled ? 'var(--accent)' : 'var(--border)',
        border: 'none', cursor: 'pointer',
        transition: 'background 200ms', display: 'flex', alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: '50%',
        background: '#fff',
        transform: enabled ? 'translateX(20px)' : 'translateX(0)',
        transition: 'transform 200ms',
        display: 'block',
      }} />
    </button>
  );
}

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

function ProviderCard({ provider, initialData, onSaved }) {
  const meta = PROVIDER_META[provider];
  const cfg = initialData?.config_json || {};

  const [enabled, setEnabled]         = useState(!!initialData?.enabled);
  const [token, setToken]             = useState('');
  const [tokenDirty, setTokenDirty]   = useState(false);
  const [org, setOrg]                 = useState(cfg.workspace_or_org || '');
  const [baseUrl, setBaseUrl]         = useState(cfg.base_url || '');
  const [bbAuthMode, setBbAuthMode]   = useState(provider === 'bitbucket' ? (cfg.auth_mode || 'basic') : 'bearer');
  const [bbUsername, setBbUsername]   = useState(provider === 'bitbucket' ? (cfg.bb_username || '') : '');
  const [bbSecretName, setBbSecretName] = useState(provider === 'bitbucket' ? (cfg.secret_name || '') : '');
  const [giteaUsername, setGiteaUsername] = useState(provider === 'gitea' ? (cfg.gitea_username || '') : '');
  const [saving, setSaving]           = useState(false);
  const [saveMsg, setSaveMsg]         = useState('');
  const [saveErr, setSaveErr]         = useState('');

  const hasExistingToken = initialData?.config_json?.access_token === '***';

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');

    const body = { enabled, workspace_or_org: org };
    if (meta.showBaseUrl) body.base_url = baseUrl;

    if (provider === 'bitbucket') {
      // The token itself is never sent — it lives in AWS Secrets Manager and is
      // read on the instance role. Only the pointer to it is stored.
      body.auth_mode = bbAuthMode;
      body.bb_username = bbUsername.trim();
      body.secret_name = bbSecretName.trim();

      if (!bbSecretName.trim()) {
        setSaveErr('AWS secret name is required — it names the secret whose value is the Bitbucket API token.');
        setSaving(false);
        return;
      }
      if (bbAuthMode === 'basic' && !bbUsername.trim()) {
        setSaveErr('Atlassian account email is required in "email + API token" mode.');
        setSaving(false);
        return;
      }
    } else if (provider === 'gitea') {
      // Gitea's PR-diff endpoint is a web route that only accepts HTTP Basic auth,
      // so a username is required alongside the token — the API alone isn't enough
      // to read a diff. The backend splices the two together for storage.
      if (!giteaUsername.trim()) {
        setSaveErr('Gitea username is required — the PR diff endpoint uses Basic auth.');
        setSaving(false);
        return;
      }
      if (!hasExistingToken && !token.trim()) {
        setSaveErr('Gitea access token is required.');
        setSaving(false);
        return;
      }
      body.gitea_username = giteaUsername.trim();
      if (tokenDirty && token.trim()) {
        body.access_token = token.trim();
      }
    } else if (tokenDirty && token.trim()) {
      body.access_token = token.trim();
    }

    try {
      await axios.put(`/repo-providers/${provider}`, body);
      setSaveMsg('Saved successfully');
      setTokenDirty(false);
      setToken('');
      if (onSaved) onSaved();
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveErr(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '18px 24px',
        borderBottom: '1px solid var(--border)',
        background: `${meta.color}12`,
      }}>
        <span style={{ color: meta.color }}>{meta.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{meta.label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {meta.description}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            fontSize: 12, color: enabled ? 'var(--success)' : 'var(--text-muted)',
            fontWeight: 600,
          }}>
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
          <ToggleSwitch enabled={enabled} onChange={() => setEnabled(v => !v)} />
        </div>
      </div>

      {/* Card body */}
      <form onSubmit={handleSave} style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Access Token / Auth */}
          {provider === 'bitbucket' ? (
            <>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Authentication Mode
                </label>
                <select
                  className="form-input"
                  style={{ width: '100%' }}
                  value={bbAuthMode}
                  onChange={e => setBbAuthMode(e.target.value)}
                >
                  <option value="basic">Atlassian email + API token</option>
                  <option value="bearer">OAuth Bearer token</option>
                </select>
              </div>
              {bbAuthMode === 'basic' && (
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                    Atlassian account email
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    style={{ width: '100%' }}
                    placeholder="you@company.com"
                    value={bbUsername}
                    onChange={e => setBbUsername(e.target.value)}
                    autoComplete="username"
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Use your Atlassian account email — not your Bitbucket username. This is
                    not a secret and is stored here; it is combined with the token from
                    Secrets Manager to form the Basic credential.
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  AWS secret name
                </label>
                <input
                  type="text"
                  className="form-input"
                  style={{ width: '100%' }}
                  placeholder="prod/productfeatures/bitbucket"
                  value={bbSecretName}
                  onChange={e => setBbSecretName(e.target.value)}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Name or ARN of the Secrets Manager secret whose plain-text value is the
                  Bitbucket API token. The token is never entered here — it is read at call
                  time on the instance role, so no AWS keys are stored either. Create the
                  token at{' '}
                  <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer">
                    Atlassian account → Security → API tokens
                  </a>
                  {' '}with <strong>Repositories: Read</strong> and <strong>Pull requests: Read</strong>,
                  then store it as the whole value of its own secret. The region comes from
                  AI Settings.
                </div>
              </div>

              <div>
                <CredentialTest
                  endpoint={`/repo-providers/${provider}/test`}
                  disabled={!bbSecretName.trim()}
                  payload={() => ({
                    secret_name:      bbSecretName.trim(),
                    workspace_or_org: org.trim(),
                    auth_mode:        bbAuthMode,
                    bb_username:      bbUsername.trim(),
                  })}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Retrieves the token on the instance role and calls the Bitbucket API with
                  it. Tests what is on screen, so you can check before saving.
                </div>
              </div>
            </>
          ) : provider === 'gitea' ? (
            <>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Gitea username
                </label>
                <input
                  type="text"
                  className="form-input"
                  style={{ width: '100%' }}
                  placeholder="your-gitea-login"
                  value={giteaUsername}
                  onChange={e => setGiteaUsername(e.target.value)}
                  autoComplete="username"
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Required. Gitea serves PR diffs from a web route that only accepts HTTP
                  Basic auth, so the username is needed in addition to the token.
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  {meta.tokenLabel}
                </label>
                <input
                  type="password"
                  className="form-input"
                  style={{ width: '100%' }}
                  placeholder={hasExistingToken ? '•••••••• (saved – enter new value to replace)' : 'Enter token...'}
                  value={token}
                  onChange={e => { setToken(e.target.value); setTokenDirty(true); }}
                  autoComplete="new-password"
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  {meta.tokenHint} On Gitea older than 1.12 the reviewer posts a single
                  summary comment instead of inline comments — that version has no
                  pull-review API.
                </div>
              </div>
            </>
          ) : (
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {meta.tokenLabel}
              </label>
              <input
                type="password"
                className="form-input"
                style={{ width: '100%' }}
                placeholder={hasExistingToken ? '•••••••• (saved – enter new value to replace)' : 'Enter token...'}
                value={token}
                onChange={e => { setToken(e.target.value); setTokenDirty(true); }}
                autoComplete="new-password"
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {meta.tokenHint}
              </div>
            </div>
          )}

          {/* Organisation / Workspace */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              {meta.orgLabel}
            </label>
            <input
              type="text"
              className="form-input"
              style={{ width: '100%' }}
              placeholder={meta.orgHint}
              value={org}
              onChange={e => setOrg(e.target.value)}
            />
          </div>

          {/* Base URL (GitLab only) */}
          {meta.showBaseUrl && (
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Instance URL
              </label>
              <input
                type="url"
                className="form-input"
                style={{ width: '100%' }}
                placeholder={meta.baseUrlPlaceholder}
                value={baseUrl}
                onChange={e => setBaseUrl(e.target.value)}
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {meta.baseUrlHint}
              </div>
            </div>
          )}
        </div>

        {saveErr && (
          <div className="error-msg" style={{ marginTop: 12 }}>{saveErr}</div>
        )}
        {saveMsg && (
          <div style={{
            marginTop: 12, padding: '8px 14px', borderRadius: 'var(--radius)',
            background: 'var(--success-bg, rgba(34,197,94,0.15))',
            color: 'var(--success)', fontSize: 13, fontWeight: 500,
          }}>
            {saveMsg}
          </div>
        )}

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ProvidersConfig() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/repo-providers');
      const map = {};
      for (const row of res.data.data || []) {
        map[row.provider] = row;
      }
      setProviders(map);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load provider config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  return (
    <div>
      <div className="page-header">
        <h2>SCM Provider Configuration</h2>
        <p>Manage access tokens and workspace settings for each code hosting provider</p>
      </div>

      <div className="page-body">
        <div className="toolbar" style={{ marginBottom: 24 }}>
          <button
            className="btn btn-ghost"
            onClick={() => navigate('/repositories')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconArrowLeft /> Back to Repositories
          </button>
        </div>

        {error && <div className="error-msg">{error}</div>}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="spinner" />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {['github', 'gitlab', 'bitbucket', 'gitea'].map(provider => (
              <ProviderCard
                key={provider}
                provider={provider}
                initialData={providers[provider]}
                onSaved={loadProviders}
              />
            ))}
          </div>
        )}

        <div style={{
          marginTop: 24, padding: '16px 20px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <strong style={{ color: 'var(--text-primary)' }}>How syncing works:</strong>{' '}
          Once you save a provider config and enable it, the{' '}
          <strong>Repository Sync</strong> job (runs every Sunday at 02:00 UTC) will fetch all
          repositories from that provider and store them here. The{' '}
          <strong>Repository Owner Sync</strong> job (Sunday at 04:00 UTC) will then update the
          owner field for each repo based on the top contributor in the last 90 days. You can
          trigger both jobs manually from the{' '}
          <button
            className="btn btn-ghost"
            style={{ fontSize: 13, padding: '0 2px', color: 'var(--accent)' }}
            onClick={() => navigate('/jobs')}
          >
            Jobs
          </button>{' '}
          page.
        </div>
      </div>
    </div>
  );
}
